import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PropertyShowcase = ({ commune }) => {
  if (!commune) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <div className="bg-surface">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16">
        <div className="flex items-center justify-between mb-12">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="Home" size={20} className="text-brand-gold" />
              <span className="text-sm font-medium text-brand-gold uppercase tracking-wide">
                Propriétés Disponibles
              </span>
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
              Biens à {commune?.name}
            </h2>
            
            <p className="text-lg text-text-secondary">
              Découvrez notre sélection de propriétés exceptionnelles dans cette commune
            </p>
          </div>
          
          <Link to="/property-search-listings" className="hidden lg:block">
            <Button variant="outline" iconName="ArrowRight" iconPosition="right">
              Voir Toutes les Propriétés
            </Button>
          </Link>
        </div>

        {/* Featured Properties Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {commune?.featuredProperties?.map((property, index) =>
          <div key={index} className="bg-white rounded-xl shadow-warm hover:shadow-deep transition-shadow duration-300 overflow-hidden group">
              {/* Property Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                src={property?.image}
                alt={property?.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                
                {/* Property Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                property?.status === 'À vendre' ? 'bg-green-100 text-green-600' :
                property?.status === 'À louer' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`
                }>
                    {property?.status}
                  </span>
                </div>
                
                {/* Video Play Button */}
                {property?.hasVideo &&
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                    <button className="bg-white/90 backdrop-blur-sm p-3 rounded-full hover:bg-white transition-colors duration-300">
                      <Icon name="Play" size={20} className="text-text-primary ml-1" />
                    </button>
                  </div>
              }
                
                {/* Favorite Button */}
                <button className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors duration-300">
                  <Icon name="Heart" size={16} className="text-text-secondary" />
                </button>
              </div>
              
              {/* Property Details */}
              <div className="p-6 border-none">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-text-primary line-clamp-1">
                    {property?.title}
                  </h3>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={14} className="text-yellow-500" />
                    <span className="text-sm text-text-secondary">{property?.rating}</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1 mb-3">
                  <Icon name="MapPin" size={14} className="text-text-secondary" />
                  <p className="text-sm text-text-secondary line-clamp-1">
                    {property?.location}
                  </p>
                </div>
                
                {/* Property Features */}
                <div className="flex items-center space-x-4 mb-4 text-sm text-text-secondary">
                  <div className="flex items-center space-x-1">
                    <Icon name="Bed" size={14} />
                    <span>{property?.bedrooms}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Bath" size={14} />
                    <span>{property?.bathrooms}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Square" size={14} />
                    <span>{property?.area}m²</span>
                  </div>
                </div>
                
                {/* Price and CTA */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-text-primary">
                      {formatPrice(property?.price)}
                    </p>
                    {property?.pricePerSqm &&
                  <p className="text-xs text-text-secondary">
                        {formatPrice(property?.pricePerSqm)}/m²
                      </p>
                  }
                  </div>
                  
                  <Link to="/individual-property-page">
                    <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right">
                      Détails
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Property Types Overview */}
        <div className="bg-white rounded-2xl p-8 shadow-warm border-none">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                Types de Biens Disponibles
              </h3>
              <p className="text-text-secondary">
                Explorez la diversité de l'offre immobilière à {commune?.name}
              </p>
            </div>
            
            <Link to="/property-search-listings">
              <Button variant="default" iconName="Search" iconPosition="left">
                Recherche Avancée
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commune?.propertyTypes?.map((type, index) =>
            <div key={index} className="text-center p-6 border border-border rounded-xl hover:shadow-warm transition-shadow duration-300">
                <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={type?.icon} size={24} className="text-brand-gold" />
                </div>
                
                <h4 className="font-semibold text-text-primary mb-2">
                  {type?.name}
                </h4>
                
                <p className="text-sm text-text-secondary mb-3">
                  {type?.description}
                </p>
                
                <div className="space-y-1">
                  <p className="text-lg font-bold text-text-primary">
                    {type?.count} biens
                  </p>
                  <p className="text-xs text-text-secondary">
                    À partir de {formatPrice(type?.startingPrice)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile CTA */}
        <div className="lg:hidden mt-8 text-center">
          <Link to="/property-search-listings">
            <Button variant="default" fullWidth iconName="Search" iconPosition="left">
              Voir Toutes les Propriétés
            </Button>
          </Link>
        </div>
      </div>
    </div>);

};

export default PropertyShowcase;